import { constructQueryParamName } from '../../../util/search';

/**
 * Helpers for the hybrid filter row + panel (browse redesign Phase 4,
 * ledger §2).
 */

const HEAD_SCOPED_LIMIT = 3;

/**
 * Head-pill rule: Condition first, then the FIRST 3 category-scoped fields in
 * listing-fields file order, then Price. Everything else lives only in the
 * slide-out panel (overflow); active overflow fields surface as pills in the
 * row.
 *
 * @param {Array} customFilters category-scoped + global listing-field filter
 *   configs in file order (the category page's availableFilters minus
 *   category/default entries)
 * @param {Array} defaultFilters validated default filter configs (price, ...)
 * @returns {{ head: Array, overflow: Array }}
 */
export const splitHeadAndOverflowFilters = (customFilters, defaultFilters) => {
  const condition = customFilters.filter(f => f.key === 'condition');
  const scoped = customFilters.filter(f => f.key !== 'condition');
  const price = (defaultFilters || []).filter(f => f.schemaType === 'price');
  const otherDefaults = (defaultFilters || []).filter(
    f => f.schemaType !== 'price' && f.schemaType !== 'category' && f.schemaType !== 'keywords'
  );

  const head = [...condition, ...scoped.slice(0, HEAD_SCOPED_LIMIT), ...price];
  const overflow = [...scoped.slice(HEAD_SCOPED_LIMIT), ...otherDefaults];
  return { head, overflow };
};

/**
 * Is a filter active in the current URL params?
 */
export const isFilterActive = (filterConfig, urlQueryParams) => {
  if (!filterConfig || !urlQueryParams) return false;
  if (filterConfig.schemaType === 'price') return urlQueryParams.price != null;
  const paramName = constructQueryParamName(filterConfig.key, filterConfig.scope || 'public');
  return urlQueryParams[paramName] != null;
};

/**
 * Narrow a filter config's enumOptions to values present in the current
 * result set (v1 presence semantics, plan Flag 1).
 *
 * - presence == null (endpoint degraded / not loaded): full list, no narrowing.
 * - A field with an ACTIVE selection keeps its full list — approximation of
 *   the faceted-search "edited field counts against other filters" exception
 *   without an extra query per field; the selected sibling options must stay
 *   pickable.
 * - Selected values are always retained even when absent from presence.
 */
export const narrowFilterOptions = (filterConfig, presence, urlQueryParams) => {
  const enumOptions = filterConfig?.enumOptions;
  if (!presence || !enumOptions || enumOptions.length === 0) return filterConfig;
  if (isFilterActive(filterConfig, urlQueryParams)) return filterConfig;

  const present = presence[filterConfig.key];
  if (!present) {
    // Key absent from the scan: nothing in the result set has this field
    return { ...filterConfig, enumOptions: [] };
  }
  const presentSet = new Set(present);
  const narrowed = enumOptions.filter(opt => presentSet.has(opt.option || opt.key));
  return { ...filterConfig, enumOptions: narrowed };
};

/**
 * Search the FULL option list by label — the panel type-ahead must reach
 * options that presence narrowing hid (safety net for the scan cap).
 */
export const searchFilterOptions = (filterConfig, searchTerm) => {
  const enumOptions = filterConfig?.enumOptions;
  if (!enumOptions || !searchTerm || !searchTerm.trim()) return null;
  const term = searchTerm.trim().toLowerCase();
  return {
    ...filterConfig,
    enumOptions: enumOptions.filter(opt =>
      String(opt.label || opt.option || '').toLowerCase().includes(term)
    ),
  };
};

/**
 * Parse the selected option keys for an enum/multi-enum filter from the URL
 * (strips the has_all/has_any search-mode prefix).
 */
export const parseSelectedValues = (filterConfig, urlQueryParams) => {
  const paramName = constructQueryParamName(filterConfig.key, filterConfig.scope || 'public');
  const raw = urlQueryParams?.[paramName];
  return raw ? String(raw).replace(/^has_(all|any):/, '').split(',').filter(Boolean) : [];
};

/**
 * Format selected option keys back into the URL value (null clears; multi-enum
 * keeps its configured search-mode prefix).
 */
export const formatFilterValue = (filterConfig, values) => {
  if (!values || values.length === 0) return null;
  const searchMode =
    filterConfig.schemaType === 'multi-enum' ? filterConfig.filterConfig?.searchMode : null;
  return searchMode ? `${searchMode}:${values.join(',')}` : values.join(',');
};

/**
 * Noun for "Type a {noun}…" type-ahead placeholders — field labels don't
 * always read naturally ("Type a made in…"), so known cases map to a real
 * noun and everything else falls back to the lowercased label.
 */
export const typeaheadNoun = label => {
  const text = String(label || '');
  if (/made\s*in/i.test(text)) return 'country';
  return text.toLowerCase();
};

/**
 * Sort button shows short labels ("High to low"); the menu keeps full labels
 * ("Price: high to low"). Ledger: Filter system — FINAL 2026-08-25.
 */
export const shortSortLabel = fullLabel => {
  const text = String(fullLabel || '');
  const idx = text.indexOf(':');
  const short = idx >= 0 ? text.slice(idx + 1).trim() : text;
  return short.charAt(0).toUpperCase() + short.slice(1);
};

/**
 * Production price-pill label (SUPERSEDES the prototype's "$0/∞" bounds):
 * "Under $X" / "$X+" / "$X–$Y". Returns null when no price filter is active.
 */
export const priceActiveLabel = priceParam => {
  if (!priceParam) return null;
  const [minRaw, maxRaw] = String(priceParam).split(',');
  const min = minRaw !== '' && minRaw != null ? Number(minRaw) : null;
  const max = maxRaw !== '' && maxRaw != null ? Number(maxRaw) : null;
  const fmt = n => `$${Number(n).toLocaleString('en-US')}`;
  if ((min == null || min === 0) && max != null) return `Under ${fmt(max)}`;
  if (min != null && max == null) return `${fmt(min)}+`;
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
  return null;
};

/**
 * Narrow a filter config's options to an explicit list of present values —
 * used for the ACTIVE-field exception, where presence is computed from a
 * facets call that omits this field's own filter. Selected values always
 * stay visible.
 */
export const narrowToValues = (filterConfig, presentValues, selectedValues = []) => {
  const enumOptions = filterConfig?.enumOptions;
  if (!enumOptions || !presentValues) return filterConfig;
  const keep = new Set([...presentValues, ...selectedValues]);
  return {
    ...filterConfig,
    enumOptions: enumOptions.filter(opt => keep.has(opt.option || opt.key)),
  };
};
